import {
  SHIP_RADIUS, SHIP_ROTATION_THRUST, SHIP_ROTATION_BRAKE_THRUST, SHIP_ROTATION_INERTIA, SHIP_MAX_ROTATION_SPEED,
  SHIP_THRUST, SHIP_INERTIA, SHIP_BRAKE_INERTIA,
  SHIP_WALL_RESTITUTION, SHIP_WALL_TANGENTIAL_FRICTION, SHIP_WALL_SPIN_COUPLING,
  SHIP_FIRE_COOLDOWN_MS, SHIP_BURST_TOKENS_MAX
} from '../constants.js';
import { bounceCircleOffWalls, wrap, normalizeDeg } from '../physics.js';

/**
 * Refill a ship's fire-token bucket up to MAX based on elapsed wall-clock ms.
 * Shared by server and client prediction so the bucket math agrees.
 */
export function refillFireTokens(ship, now) {
  if (!Number.isFinite(now)) return;
  const dt = Math.max(0, now - ship.lastTokenUpdateMs);
  ship.fireTokens = Math.min(
    SHIP_BURST_TOKENS_MAX,
    ship.fireTokens + dt / SHIP_FIRE_COOLDOWN_MS
  );
  ship.lastTokenUpdateMs = now;
}

function approachZero(value, amount) {
  if (value > 0) return Math.max(0, value - amount)
  if (value < 0) return Math.min(0, value + amount)
  return 0
}

/**
 * Authoritative ship state. Runs identically on server (main sim) and on
 * client (prediction of the local player). No canvas / no RNG.
 */
export default class Ship {
  constructor({
    id,
    position,
    rotation = 0,
    color = '#fff',
    name = '',
    teamId = null,
    teamName = null
  }) {
    this.id = id;
    this.position = { x: position.x, y: position.y };
    this.velocity = { x: 0, y: 0 };
    this.rotation = rotation;
    this.angularVelocity = 0;
    this.radius = SHIP_RADIUS;
    this.color = color;
    this.name = name;
    this.teamId = teamId;
    this.teamName = teamName;
    this.alive = true;
    this.invulnerableUntil = 0;
    this.respawnAt = 0;       // server wall-clock ms when dead ship respawns
    this.lastShotAt = 0;      // server wall-clock ms of last bullet fired
    this.lastAckedSeq = 0;    // most recent input seq processed (for client reconciliation)
    this.fireTokens = SHIP_BURST_TOKENS_MAX; // token-bucket charges; one consumed per shot
    this.lastTokenUpdateMs = 0;              // wall-clock anchor for refill math
    this.shieldHealth = 0;                   // shields active when > 0; absorb 1 hit each
    this.shieldMax = 0;                      // 0 means shield system disabled this match
    /** @type {{left:boolean,right:boolean,up:boolean,down:boolean,firePresses:number}} */
    this.input = { left: false, right: false, up: false, down: false, firePresses: 0 };
    // client-only hint: was thrust applied on the most recent update?
    this.thrustingThisTick = false;
  }

  hasShield() {
    return this.shieldHealth > 0;
  }

  applyInput(keys) {
    this.input.left = !!keys.left;
    this.input.right = !!keys.right;
    this.input.up = !!keys.up;
    this.input.down = !!keys.down;
    const presses = Number(keys.firePresses);
    this.input.firePresses = Number.isFinite(presses) && presses > 0 ? Math.floor(presses) : 0;
  }

  setState({ x, y, vx, vy, rotation, av = 0, shieldHealth, shieldMax }) {
    this.position.x = x; this.position.y = y;
    this.velocity.x = vx; this.velocity.y = vy;
    this.rotation = rotation;
    this.angularVelocity = av;
    if (Number.isFinite(shieldHealth)) this.shieldHealth = shieldHealth;
    if (Number.isFinite(shieldMax)) this.shieldMax = shieldMax;
  }

  // Pure physics step. dt == 1 means one 60 Hz tick.
  /** @returns {{ wallBounce: boolean }} */
  update(dt, worldBounds) {
    this.thrustingThisTick = false;
    if (!this.alive) return { wallBounce: false };

    if (this.input.left) this.angularVelocity -= SHIP_ROTATION_THRUST * dt;
    if (this.input.right) this.angularVelocity += SHIP_ROTATION_THRUST * dt;
    if (this.input.down) {
      this.angularVelocity = approachZero(
        this.angularVelocity,
        SHIP_ROTATION_BRAKE_THRUST * dt
      )
    }
    if (this.angularVelocity > SHIP_MAX_ROTATION_SPEED) this.angularVelocity = SHIP_MAX_ROTATION_SPEED;
    if (this.angularVelocity < -SHIP_MAX_ROTATION_SPEED) this.angularVelocity = -SHIP_MAX_ROTATION_SPEED;

    /**
     * Integrate heading before thrust so thrust and bullet spawn use the same
     * facing as the nose this tick (avoids firing at an angle while spinning).
     */
    this.rotation += this.angularVelocity * dt;

    if (this.input.up) {
      const rad = -this.rotation * Math.PI / 180;
      this.velocity.x -= Math.sin(rad) * SHIP_THRUST * dt;
      this.velocity.y -= Math.cos(rad) * SHIP_THRUST * dt;
      this.thrustingThisTick = true;
    }
    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;
    this.angularVelocity *= Math.pow(SHIP_ROTATION_INERTIA, dt);
    if (this.angularVelocity > SHIP_MAX_ROTATION_SPEED) this.angularVelocity = SHIP_MAX_ROTATION_SPEED;
    if (this.angularVelocity < -SHIP_MAX_ROTATION_SPEED) this.angularVelocity = -SHIP_MAX_ROTATION_SPEED;
    this.velocity.x *= Math.pow(SHIP_INERTIA, dt);
    this.velocity.y *= Math.pow(SHIP_INERTIA, dt);
    if (this.input.down) {
      this.velocity.x *= Math.pow(SHIP_BRAKE_INERTIA, dt);
      this.velocity.y *= Math.pow(SHIP_BRAKE_INERTIA, dt);
    }

    this.rotation = normalizeDeg(this.rotation);
    let wallBounce = false;
    if (worldBounds.toroidalMap === false) {
      wallBounce = bounceCircleOffWalls(this, worldBounds, {
        restitution: SHIP_WALL_RESTITUTION,
        tangentialFriction: SHIP_WALL_TANGENTIAL_FRICTION,
        spinCoupling: SHIP_WALL_SPIN_COUPLING,
        spinDamping: 0.96
      });
      if (this.angularVelocity > SHIP_MAX_ROTATION_SPEED) this.angularVelocity = SHIP_MAX_ROTATION_SPEED;
      if (this.angularVelocity < -SHIP_MAX_ROTATION_SPEED) this.angularVelocity = -SHIP_MAX_ROTATION_SPEED;
    } else {
      wrap(this.position, worldBounds.width, worldBounds.height, 0);
    }
    return { wallBounce };
  }
}
